<template>
  <div class="entity-list-page scav-roster">
    <header class="list-header">
      <div>
        <router-link to="/settings/marketing" class="back-link">← Marketing</router-link>
        <h1>SCAV roster</h1>
        <p class="list-sub">
          People on the SCAV MemberClicks roster — used to build Facebook and Instagram
          email-match audiences (members vs everyone else on the roster).
        </p>
      </div>
    </header>

    <div class="filters-bar">
      <input
        v-model="emailFilter"
        type="search"
        placeholder="Search email…"
        @input="debouncedLoad"
      />
      <select v-model="segmentFilter" @change="resetAndLoad">
        <option value="">All on roster</option>
        <option value="members">Active members</option>
        <option value="non_members">Non-members on roster</option>
      </select>
      <button class="btn-refresh" :disabled="loading" type="button" @click="loadRows">
        <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
        Refresh
      </button>
    </div>

    <div v-if="loading && rows.length === 0" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i> Loading roster…
    </div>

    <div v-else-if="error" class="error-state">{{ error }}</div>

    <div v-else class="table-container">
      <div class="table-header">
        <span>{{ total }} on roster</span>
      </div>
      <table class="contacts-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Organization</th>
            <th>Member status</th>
            <th>Segment</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.id">
            <td>{{ row.display_name || '—' }}</td>
            <td>{{ row.email }}</td>
            <td>{{ row.organization_name || '—' }}</td>
            <td>{{ row.member_status || '—' }}</td>
            <td>{{ row.is_active_member ? 'Member' : 'Non-member' }}</td>
          </tr>
        </tbody>
      </table>

      <div v-if="hasMore || offset > 0" class="pagination">
        <button type="button" :disabled="loading || offset === 0" @click="prevPage">
          Previous
        </button>
        <span>{{ offset + 1 }}–{{ offset + rows.length }} of {{ total }}</span>
        <button type="button" :disabled="loading || !hasMore" @click="nextPage">
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { debounce } from 'lodash-es';
import { UI_CONSTANTS } from '@/constants';
import api from '@/services/api';

const loading = ref(false);
const error = ref(null);
const rows = ref([]);
const total = ref(0);
const offset = ref(0);
const limit = ref(UI_CONSTANTS.DEFAULT_PAGE_SIZE);
const hasMore = ref(false);
const emailFilter = ref('');
const segmentFilter = ref('');

async function loadRows() {
  loading.value = true;
  error.value = null;
  try {
    const params = {
      limit: limit.value,
      offset: offset.value,
    };
    if (emailFilter.value) params.email = emailFilter.value;
    if (segmentFilter.value) params.segment = segmentFilter.value;

    const response = await api.get('/scav-marketing/roster/', { params });
    rows.value = response.data?.results || [];
    total.value = response.data?.total ?? rows.value.length;
    hasMore.value = offset.value + rows.value.length < total.value;
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to load SCAV roster';
  } finally {
    loading.value = false;
  }
}

const debouncedLoad = debounce(() => {
  offset.value = 0;
  loadRows();
}, UI_CONSTANTS.SEARCH_DEBOUNCE_MS);

function resetAndLoad() {
  offset.value = 0;
  loadRows();
}

function prevPage() {
  offset.value = Math.max(0, offset.value - limit.value);
  loadRows();
}

function nextPage() {
  offset.value += limit.value;
  loadRows();
}

onMounted(() => {
  loadRows();
});
</script>

<style scoped>
.scav-roster {
  padding: 1.5rem;
}

.list-header h1 {
  margin: 0.25rem 0 0;
}

.list-sub {
  margin: 0.35rem 0 0;
  color: var(--p-ink-muted, #64748b);
  max-width: 42rem;
  line-height: 1.45;
}

.back-link {
  font-size: 0.875rem;
  color: var(--p-ink-muted, #64748b);
  text-decoration: none;
}

.filters-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin: 1rem 0;
  align-items: center;
}

.filters-bar input,
.filters-bar select {
  padding: 0.4rem 0.6rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
}

.btn-refresh {
  padding: 0.4rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
}

.table-container {
  overflow-x: auto;
}

.contacts-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.contacts-table th,
.contacts-table td {
  text-align: left;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e2e8f0;
}

.pagination {
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-top: 1rem;
}

.loading-state,
.error-state {
  padding: 2rem;
  color: var(--p-ink-muted, #64748b);
}

.error-state {
  color: #b91c1c;
}
</style>
